import React, { useState, useCallback } from 'react';
import styles from './profile.module.css';
import KYCModal from './KYCModal';
import BankDetailsModal from './BankDetailsModal';
import { useSubmitKYCDetails, useSubmitBankDetails } from '../../api';
import { useDispatch } from 'react-redux';
import { login as loginAction } from '../../redux/slice/authSlice';

const KYCDetails = ({ user }) => {
  const kycDetails = user?.kycDetails?.[0];
  const bankDetails = user?.bankDetails?.[0];

  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const dispatch = useDispatch();

  const submitKYCMutation = useSubmitKYCDetails();
  const submitBankMutation = useSubmitBankDetails();

  const handleKYCSubmit = useCallback(
    async (kycData) => {
      console.log('handleKYCSubmit called with:', kycData);
      console.log('User ID:', user?._id);

      if (!user?._id) {
        throw new Error('User ID not found');
      }

      try {
        const response = await submitKYCMutation.mutateAsync({
          userId: user._id,
          kycData,
        });

        // Update Redux state with new user data
        dispatch(loginAction({ user: response.user, token: user.token }));
        localStorage.setItem('user', JSON.stringify(response.user));
        return response;
      } catch (error) {
        console.error('KYC submission error:', error);
        throw error;
      }
    },
    [user?._id, user?.token, submitKYCMutation, dispatch]
  );

  const handleBankSubmit = useCallback(
    async (bankData) => {
      console.log('handleBankSubmit called with:', bankData);
      console.log('User ID:', user?._id);

      if (!user?._id) {
        throw new Error('User ID not found');
      }

      try {
        const response = await submitBankMutation.mutateAsync({
          userId: user._id,
          bankData,
        });

        // Update Redux state with new user data
        dispatch(loginAction({ user: response.user, token: user.token }));
        localStorage.setItem('user', JSON.stringify(response.user));
        return response;
      } catch (error) {
        console.error('Bank submission error:', error);
        throw error;
      }
    },
    [user?._id, user?.token, submitBankMutation, dispatch]
  );

  return (
    <div className={styles.personalInfo}>
      <div className={styles.sectionHeader}>
        <h2>KYC & Verification Details</h2>
      </div>

      <div className={styles.infoCard}>
        {/* KYC Information */}
        <div className={styles.infoSection}>
          <h3 className={styles.sectionTitle}>KYC Information</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>KYC Status</span>
              <span
                className={`${styles.infoValue} ${styles.statusBadge} ${
                  kycDetails?.verified === 'VERIFIED'
                    ? styles.active
                    : kycDetails?.verified === 'PENDING'
                      ? styles.warning
                      : kycDetails?.verified === 'REJECTED'
                        ? styles.inactive
                        : styles.inactive
                }`}
              >
                {kycDetails?.verified || 'Not Submitted'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Aadhaar Number</span>
              <span className={styles.infoValue}>
                {kycDetails?.adhaarNo
                  ? `${kycDetails.adhaarNo.slice(0, 4)}****${kycDetails.adhaarNo.slice(-4)}`
                  : 'Not provided'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Aadhaar Front Image</span>
              <span className={styles.infoValue}>
                {kycDetails?.adhaarFrontImg ? (
                  <a
                    href={kycDetails.adhaarFrontImg}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    View Document
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Aadhaar Back Image</span>
              <span className={styles.infoValue}>
                {kycDetails?.adhaarbackImg ? (
                  <a
                    href={kycDetails.adhaarbackImg}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    View Document
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className={styles.infoSection}>
          <h3 className={styles.sectionTitle}>Bank Information</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Bank Verification Status</span>
              <span
                className={`${styles.infoValue} ${styles.statusBadge} ${
                  bankDetails?.verified === 'VERIFIED'
                    ? styles.active
                    : bankDetails?.verified === 'PENDING'
                      ? styles.warning
                      : bankDetails?.verified === 'REJECTED'
                        ? styles.inactive
                        : styles.inactive
                }`}
              >
                {bankDetails?.verified || 'Not Submitted'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Bank Name</span>
              <span className={styles.infoValue}>{bankDetails?.bankName || 'Not provided'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Account Number</span>
              <span className={styles.infoValue}>
                {bankDetails?.accountNumber
                  ? `****${bankDetails.accountNumber.slice(-4)}`
                  : 'Not provided'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>IFSC Code</span>
              <span className={styles.infoValue}>{bankDetails?.ifsc || 'Not provided'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Account Holder Name</span>
              <span className={styles.infoValue}>
                {bankDetails?.accountHolderName || 'Not provided'}
              </span>
            </div>
          </div>
        </div>

        {/* Verification Actions */}
        <div className={styles.infoSection}>
          <h3 className={styles.sectionTitle}>Verification Actions</h3>
          <div className={styles.actionButtons}>
            {!kycDetails && (
              <button className={styles.verifyBtn} onClick={() => setShowKYCModal(true)}>
                Submit KYC Documents
              </button>
            )}
            {!bankDetails && (
              <button className={styles.verifyBtn} onClick={() => setShowBankModal(true)}>
                Add Bank Details
              </button>
            )}
            {kycDetails?.verified === 'PENDING' && (
              <div className={styles.pendingNotice}>
                <p>Your KYC documents are under review. Please wait for verification.</p>
              </div>
            )}
            {bankDetails?.verified === 'PENDING' && (
              <div className={styles.pendingNotice}>
                <p>Your bank details are under review. Please wait for verification.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KYC Modal */}
      <KYCModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        onSubmit={handleKYCSubmit}
      />

      {/* Bank Details Modal */}
      <BankDetailsModal
        isOpen={showBankModal}
        onClose={() => setShowBankModal(false)}
        onSubmit={handleBankSubmit}
      />
    </div>
  );
};

export default KYCDetails;
